import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import OrderSummary from './OrderSummary';
import PaymentForm from './PaymentForm';
import LegSummary from './LegSummary';
import ReferralCard from './ReferralCard';
import ShippingFlowFooter from '../../components/ShippingFlowFooter';
import useOrderApi from '../../hooks/useOrderApi';
import { useTheme } from '../../context/ThemeContext';
import { useFormStore } from '../../stores/formData';
import {
  tokenizeAndCreateBraintreePaymentMethod as tokenizeBraintree,
} from './utils/braintree';
import LoadingState from '../../components/LoadingState';
import BackButton from '../../components/BackButton';
import FullScreenSpinner from '../../components/FullScreenSpinner';
import { generateFormSignature } from '../../utils/formSignature';
import CoverageCard from './CoverageCard';

const ShippingFlowPayment = ({ 
  onClickBack,
  onClickContinue,
  onEditLeg
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [creatingCart, setCreatingCart] = useState(false);  
  const [updatingCart, setUpdatingCart] = useState(false);
  const [updatingSidebarCart, setUpdatingSidebarCart] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);
  const [orderProgress, setOrderProgress] = useState(null);
  const [applyingPromoCode, setApplyingPromoCode] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState(null);
  const [cartNeedsUpdate, setCartNeedsUpdate] = useState(false);
  const cartUpdateTimeoutRef = useRef(null);
  const isUpdatingCartRef = useRef(false);
  const updateQueueRef = useRef([]);

  const [referralData, setReferralData] = useState({
    workingWithAgency: 'no',
    travelNetwork: '',
    travelAgency: '',
    agentName: '',
    iataNumber: ''
  });

  const [paymentData, setPaymentData] = useState({
    firstName: '',
    lastName: '',
    number: '',
    expiry: '',
    cvv: '',
    billingCountry: 'US',
    billingZipCode: ''
  });

  const [appliedReferral, setAppliedReferral] = useState(null);
  
  const actions = useFormStore((state) => state.actions);
  const helpers = useFormStore((state) => state.helpers);
  const formData = useFormStore((state) => state.formData);
  const formDataRef = useRef(formData);

  const {
    createCart,
    setCartUser,
    updateCart,
    getGiftCard,
    updateCartFromFormData,
    createOrder,
    createReferral,
    createUserPaymentMethod
  } = useOrderApi();

  const [creatingOrder, setCreatingOrder] = useState(false);
  const [cartError, setCartError] = useState(null); 

  useEffect(() => {
    actions.form.setCurrentPage(2, 'payment');
  }, []);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if(formData.customerInfo?.authToken?.length) {
      setIsAuthenticated(true);
    }
  }, [formData.customerInfo]);

  const handleInsuranceChange = useCallback(() => {
    console.log('Insurance changed, scheduling cart update');
    
    // Clear any pending update
    if (cartUpdateTimeoutRef.current) {
      clearTimeout(cartUpdateTimeoutRef.current);
    }
    
    // Schedule update after 300ms of no changes
    cartUpdateTimeoutRef.current = setTimeout(() => {
      console.log('Debounce completed, triggering cart update');
      setCartNeedsUpdate(true);
    }, 300);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (cartUpdateTimeoutRef.current) {
        clearTimeout(cartUpdateTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const checkCart = async () => {
      const currentFormData = formDataRef.current;
      if (currentFormData.cart?.items?.length > 0 && currentFormData.formSignature) {
        const currentSignature = currentFormData.formSignature;
        const newSignature = await generateFormSignature(currentFormData);
        if (currentSignature !== newSignature) {
          setCartNeedsUpdate(true);
        }
      }
    };
    checkCart();
  }, []); 

  const handleCardDataChange = useCallback((updatedCardData) => {
    setPaymentData(prev => {
      const newValue = { ...prev, ...updatedCardData};
      return newValue;
    });
  }, [setPaymentData]);

  const handleReferralDataChange = useCallback((updatedReferralData) => {
    setReferralData(prev => ({
      ...prev,
      ...updatedReferralData
    }));
  }, [setReferralData]);

  // Process next update in queue
  const processNextUpdate = useCallback(() => {
    if (updateQueueRef.current.length === 0 || isUpdatingCartRef.current) {
      return;
    }

    // Get next update from queue
    updateQueueRef.current.shift();
    console.log(`Processing queued update (${updateQueueRef.current.length} remaining in queue)`);
    
    // Trigger the update
    setCartNeedsUpdate(true);
  }, []);

  // Theme-based styling
  const containerClassName = clsx(
    'min-h-screen',
    {
      'bg-slate-900': isDark,
      'bg-white': !isDark
    }
  );

  const mainContentClassName = clsx(
    'max-w-7xl mx-auto p-4 md:p-6 min-h-screen'
  );

  const leftColumnClassName = clsx(
    'space-y-6'
  );

  const rightColumnClassName = clsx(
    'md:h-[900px] md:sticky md:top-6 md:self-start'
  );

  const travelerExists = true; // traveler will be created if it doesn't exist

  useEffect(() => {
    const initCart = async () => {
      if(!formData.cart.id && !creatingCart && !applyingPromoCode) {
        setCreatingCart(true);
        try {
          // Use onBehalfUser if set, otherwise use customerInfo, if admin id then
          // customer id must be the admin
          const userId = formData.onBehalfUser?.id || formData.customerInfo?.id;

          const newCart = await createCart(
            formData.brandId, 
            userId,
            formData.customerInfo?.id || null
          );
          if(newCart && newCart.id) {
            actions.form.updateField('cart', newCart);
          } 
        } catch (error) {
          console.log('Error creating cart:', error);
          setCartError('Error creating cart. Please check your information and try again.');
        }
        finally {
          setCreatingCart(false);
        }
      }
    };
    initCart();
  }, [
    creatingCart,
    createCart,
    applyingPromoCode,
    formData.cart.id,
    actions
  ]);

  useEffect(() => {
    const updateCart = async () => {
      if (isUpdatingCartRef.current) {
        console.log('Cart update already in progress, skipping main update');
        return;
      }

      if(formData.traveler
        && formData.cart.id 
        && !creatingCart
        && !applyingPromoCode
        && !promoCodeError) {
       
        isUpdatingCartRef.current = true;
        setUpdatingCart(true);

        const currentSignature = formData.formSignature || '';
        const newSignature = await generateFormSignature(formData);
        
        if(currentSignature === newSignature) {
          console.log('Signature unchanged, skipping main cart update');
          isUpdatingCartRef.current = false;
          setUpdatingCart(false);
          // Check queue even on skip
          processNextUpdate();
          return;
        }
        
        actions.form.updateField('formSignature', newSignature);
        setCartError(null);

        try {
          console.log('Starting main cart update...');
          const updatedCart = await updateCartFromFormData(formData);
          if(updatedCart && updatedCart.id === formData.cart.id) {
            actions.form.updateField('cart', updatedCart);
          }
          console.log('Main cart update complete.');
        } catch (error) {
          console.log('Error updating cart:', error);
          setCartError('Error updating cart. Please check your information and try again.');
        } finally {
          isUpdatingCartRef.current = false;
          setUpdatingCart(false);
          
          // Process next queued update if any
          processNextUpdate();
        }
      }
    };
    updateCart();
  }, [
    creatingCart,
    formData.traveler,
    formData.authToken,
    formData.cart.id,
    applyingPromoCode,
    actions,
    processNextUpdate
  ]);

  useEffect(() => {
    if (!cartNeedsUpdate) return;

    const triggerCartUpdate = async () => {
      // If main update is running, add to queue
      if (isUpdatingCartRef.current) {
        console.log('Update in progress, adding to queue...');
        updateQueueRef.current.push({ type: 'sidebar', timestamp: Date.now() });
        console.log(`Queue length: ${updateQueueRef.current.length}`);
        setCartNeedsUpdate(false);
        return;
      }

      console.log('Starting sidebar cart update...');
      isUpdatingCartRef.current = true;
      setUpdatingSidebarCart(true);
      setCartError(null);
      
      try {
        const formData = useFormStore.getState().formData; // Get fresh snapshot
        const updatedCart = await updateCartFromFormData(formData);
        if (updatedCart?.id === formData.cart.id) {
          actions.form.updateField('cart', updatedCart);
        }
        console.log('Sidebar cart update complete.');
      } catch (error) {
        console.log('Error updating cart:', error);
        setCartError('Error updating cart. Please check your information and try again.');
      } finally {
        isUpdatingCartRef.current = false;
        setUpdatingSidebarCart(false);
        setCartNeedsUpdate(false);
        
        // Process next queued update if any
        processNextUpdate();
      }
    };
    
    triggerCartUpdate();
  }, [cartNeedsUpdate, actions.form.updateField, updateCartFromFormData, processNextUpdate]);

  const handleCreatePaymentMethod = useCallback((paymentData) => {
    const paymentMethodCreate = async (paymentData) => {
      const billingAddress = {
        billingAddress: {
          postalCode: paymentData.billingZipCode,
          countryCodeAlpha2: paymentData.billingCountry,
        },
      };
      try {
        const response = await tokenizeBraintree({
            tokenizationKey: formData.tokenizationKey,
            authToken: formData.authToken,
            onBehalfUserId: formData.onBehalfUser?.id || null,
            card: {
              number: paymentData.number,
            cvv: paymentData.cvv,
            expirationDate: paymentData.expirationDate,
            ...billingAddress
          },
        });
        const { data } = response;
        actions.form.updateField('cardDetails', {
          brand: data.card_brand,
          name: paymentData.cardHolderName,
          number: data.display_name,
          expiry: paymentData.expirationDate,
          cvv: '***'
        });
        const paymentMethod = {
          id: data.id,
          cardholderName: paymentData.name,
          payerEmail: paymentData.email,
          lastFour: data.last4,
          expiryDate: paymentData.expirationDate,
          billingCountry: paymentData.billingCountry,
          billingZipCode: paymentData.billingZipCode
        }

        setUpdatingCart(true);
        await setCartUser(
          formData.authToken,
          formData.cart,
          formData.onBehalfUser?.id || formData.customerInfo?.id
        );
        const results = await updateCartFromFormData(formData);
        if (results?.id === formData.cart.id) {
          actions.form.updateField('cart', results);
        }
        setUpdatingCart(false);
        return Promise.resolve(paymentMethod);
      } catch (error) {
        console.log('Error creating payment method:', error);
        return Promise.resolve({ success: false });
      }
    };
    return paymentMethodCreate(paymentData);
  }, [
    formData.cart,
    actions
  ]);


  useEffect(() => {
    if(applyingPromoCode) {
      const applyPromoCodeToCart = async () => {
        setUpdatingCart(true);
        try {
          setPromoCodeError(null);

          let giftCard = null;
          try {
            giftCard = await getGiftCard(formData.brandId, formData.couponCode);
          } catch (error) {
            console.log('No gift card found for code:', formData.couponCode);
          }
          const payload = 
            giftCard && giftCard.id ? 
            { gift_card_ids: [giftCard.id] } : 
            { coupon_code: formData.couponCode };

          const results = await updateCart(formData.authToken, formData.cart, {
            cart: payload
          });
          if(results?.id === formData.cart.id) {
            actions.form.updateField('cart', results);
            if(giftCard && giftCard.id) {
              actions.form.updateField('giftCard', giftCard);
            } else {
              actions.form.updateField('giftCard', null);
            }
            setPromoCodeError(null);
          }
        } catch (error) {
          console.log('Error applying promo code to cart:', error);
          let error_text = 'Error applying promo code. Please try again.';
          try {
            const error_data = JSON.parse(error.message);
            error_text = error_data.errors.join(' ') || error_text;
          }
          catch (e) {
            console.error('Error parsing error response:', e);
          }
          setPromoCodeError(error_text);
        }
        finally {
          setUpdatingCart(false);
          setApplyingPromoCode(false);
        }
      }
      applyPromoCodeToCart();
    } 
  }, [applyingPromoCode, formData?.couponCode, actions]);

  const handleApplyPromoCode = useCallback((couponCode) => {
    setPaymentData(prev => ({ ...prev, couponCode }));
    actions.form.updateFields({ couponCode: couponCode });
    setApplyingPromoCode(true);
  }, [actions, paymentData]);

  const handleRemovePromoCode = useCallback(() => {
    setPaymentData(prev => ({ ...prev, couponCode: '' }));
    actions.form.updateFields({ couponCode: '' });
    setApplyingPromoCode(true);
  }, [actions, paymentData]);

  const handleApplyReferral = useCallback(async (referralCode) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock successful application
    const mockReferral = {
      code: referralCode,
      discount: 1000 // $10.00 in cents
    };
    
    setAppliedReferral(mockReferral);
    
    // Update formData with discount
    actions.form.updateFields({
      referralDiscount: mockReferral.discount,
      appliedReferral: mockReferral
    });
  }, [actions]);

  const handleCreateOrder = useCallback(() => {
    const orderCreate = async () => {
      setCreatingOrder(true);
      setOrderProgress(null);
      setOrderError(null);
      setCartError(null);

      setUpdatingCart(true);
      setOrderProgress('Updating your shopping cart...');
      await setCartUser(
        formData.authToken,
        formData.cart,
        formData.onBehalfUser?.id || formData.customerInfo?.id
      );
      const results = await updateCartFromFormData(formData);
      if(results?.id === formData.cart.id) {
        actions.form.updateField('cart', results);
      }
      setUpdatingCart(false);
      setOrderProgress('Processing your payment...');

      const billingAddress = {
        billingAddress: {
          postalCode: paymentData.billingZipCode,
          countryCodeAlpha2: paymentData.billingCountry,
        },
      };
      try {
        let paymentMethod = null;
        if (paymentData.paymentType == 'previous-card' && paymentData.previousCard) {
          if(paymentData.billingCountry?.length == 0 || paymentData.billingZipCode?.length == 0) {
            setOrderError('Billing country and zip code are required for previous card payments.'); 
            throw new Error('Billing country and zip code are required for previous card payments.');
          }
          paymentMethod = paymentData.previousCard;
        } else if (paymentData.paymentType == 'club-billing' && paymentData.clubBilling) {
          const api_params = {
            brand_id: formData.brandId,
            payment_method: {
              payment_gateway: 'club_billing',
              params: {
                club_id: paymentData.clubBilling?.club_id,
                account_number: paymentData.clubBilling?.account_number,
              }
            }
          };
          const response = await createUserPaymentMethod(
            formData,
            api_params
          );
          if ('id' in response) {
            paymentMethod = response;
          } else {
            throw new Error('Failed to create payment method. Please try again.');
          }
        } 
        else 
        {
          const response = await tokenizeBraintree({
              tokenizationKey: formData.tokenizationKey,
              authToken: formData.authToken,
              card: {
                number: paymentData.number,
              cvv: paymentData.cvv,
              expirationDate: paymentData.expiry,
              ...billingAddress
            },
          });
          const { data } = response;
          actions.form.updateField('cardDetails', {
            brand: data.card_brand,
            name: paymentData.cardHolderName || `${paymentData.firstName} ${paymentData.lastName}`,
            number: data.display_name,
            expiry: paymentData.expiry,
            cvv: '***'
          });
          
          paymentMethod = {
            id: data.id,
            cardholderName: `${paymentData.firstName} ${paymentData.lastName}`,
            payerEmail: formData.customerInfo?.email,
            lastFour: data.last4,
            expiryDate: paymentData.expiry, 
            billingCountry: paymentData.billingCountry,
            billingZipCode: paymentData.billingZipCode
          };
        }

        const order = await createOrder(formData, paymentMethod);
        if(order?.id && order?.order_id) {
          if(referralData.workingWithAgency === 'yes') {
            try {
              await createReferral(formData.authToken, {
                order_id: order.order_id,
                travel_network: referralData.travelNetwork,
                travel_company: referralData.travelCompany,
                agent_name: referralData.agentName,
                iata_number: referralData.iataNumber
              });
            } catch (error) { 
              console.error('Error creating referral:', error);
            }
          }
          setOrderCreated(true);
          setOrderProgress('Order created successfully! Redirecting to receipt...');
          const receiptUrl = `/payments/receipt?csr&order_id=${order.order_id}&utm_nooverride=1`;
          window.location.href = receiptUrl;
        }
      } catch (error) {
        setOrderError(error.message || 'Failed to create order. Please try again.');
        console.error('Error creating order:', error);
      } finally {
        setCreatingOrder(false);
      }
    };
    orderCreate();
  }, [
    formData.authToken,
    formData.paymentMethod,
    paymentData,
    referralData,
    formData.cart
  ]);

  const hasCart = useMemo(() => 
    !!formData?.cart?.id && 
    !!formData?.cart?.items && formData?.cart?.items?.length > 0, 
    [formData?.cart]
  );
  const hasPaymentData = useMemo(() => {
    return paymentData?.firstName?.length > 0 &&
      paymentData?.lastName?.length > 0 &&
      paymentData?.number?.length > 0 &&
      paymentData?.expiry?.length > 0 &&
      paymentData?.cvv?.length > 0 &&
      paymentData?.billingZipCode?.length > 0 &&
      paymentData?.billingCountry?.length > 0;
  }, [paymentData]);

  const canBookOrder = useMemo(() =>
    isAuthenticated && travelerExists && hasPaymentData && !updatingCart
  , [isAuthenticated, travelerExists, hasPaymentData, updatingCart]);

  // Derive applied promo from cart state and formData
  const appliedPromo = useMemo(() => {
    const cart = formData.cart;
    
    // Gift cards are NOT stored in cart structure - they're applied at order time
    // Check if we have a gift card object in formData (set when successfully applied)
    if (formData.giftCard?.id && formData.couponCode) {
      return {
        type: 'gift_card',
        code: formData.giftCard.code || formData.couponCode,
        applied: true
      };
    }
    
    // Coupons ARE stored in cart - check for coupon on any cart item
    const hasCoupon = cart?.items?.some(item => 
      item.products?.some(product => product.coupon_id)
    );
    
    if (hasCoupon && formData.couponCode) {
      return {
        type: 'coupon',
        code: formData.couponCode,
        applied: true
      };
    }
    
    return {
      type: null,
      code: null,
      applied: false
    };
  }, [formData.cart, formData.couponCode, formData.giftCard]);

  // if ((creatingCart || updatingCart) && !applyingPromoCode && !appliedPromoCode) {
  //   return <FullScreenSpinner 
  //     text={'Updating your cart...'} 
  //   />;
  // }

  if (creatingOrder) {
    return <FullScreenSpinner 
      text={orderProgress || 'Processing your order...'} 
    />;
  }

  if (orderCreated) {
    return <FullScreenSpinner 
      text={'Order created successfully! Redirecting...'} 
    />;
  }

  return (
    <div className={containerClassName}>
      <div className={mainContentClassName}>
        <BackButton onBack={onClickBack} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:items-start">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2">
            <div className={leftColumnClassName}>
              {isAuthenticated && creatingCart && (
                <LoadingState
                  className="border-none" message="Creating your cart..."
                />
              )}

              {cartError && (
                <div className={clsx(
                  'mt-4 p-3 rounded-lg border',
                  {
                    'bg-red-50 border-red-200 text-red-600': !isDark,
                    'bg-red-900/20 border-red-600 text-red-400': isDark
                  }
                )}>
                  {cartError}
                </div>
              )}

              {/* Leg Summary Cards - Only show if authenticated and traveler exists */}
              {isAuthenticated && travelerExists && hasCart && (
                <div className="space-y-4">
                  <div className="flex flex-col justify-start md:flex-row md:justify-between md:items-center mb-10">
                    <h2 className={clsx(
                      'text-xl font-semibold',
                      {
                        'text-slate-200': isDark,
                        'text-gray-900': !isDark
                      }
                    )}>
                      Payment
                    </h2>
                  </div>
                  {formData.legs.map((leg, index) => (
                    <LegSummary
                      key={index}
                      leg={leg}
                      legIndex={index}
                      onEdit={onEditLeg}
                    />
                  ))}
                </div>
              )}

              {isAuthenticated && formData.legs && formData.legs.length > 0 && (
                <CoverageCard
                  isExpanded={true} 
                  onInsuranceChange={handleInsuranceChange}
                  updatingCart={updatingCart || updatingSidebarCart || isUpdatingCartRef.current}
                />
              )}

              {/* Referral Card - Only show if authenticated and traveler exists */}
              {isAuthenticated && travelerExists && hasCart && (
                <ReferralCard
                  actions={actions}
                  helpers={helpers}
                  onApplyReferral={handleApplyReferral}
                  onChangeReferralData={handleReferralDataChange}
                  appliedReferral={appliedReferral}
                  isExpanded={true}
                />
              )}

              {isAuthenticated && travelerExists && hasCart && (
                <PaymentForm
                  formData={formData}
                  actions={actions}
                  helpers={helpers}
                  onCardDataChange={handleCardDataChange}
                  onApplyPromoCode={handleApplyPromoCode}
                  onRemovePromoCode={handleRemovePromoCode}
                  createPaymentMethod={handleCreatePaymentMethod}
                  onClickContinue={handleCreateOrder}
                  onClickBack={onClickBack}
                  creatingOrder={creatingOrder}
                  promoCodeError={promoCodeError}
                  applyingPromoCode={applyingPromoCode}
                  appliedPromoCode={appliedPromo.applied}
                  appliedCode={appliedPromo.code}
                  cartError={cartError}
                  orderError={orderError}
                  orderProgress={orderProgress}
                />
              )}
              
              {/* Complete Booking Button - Only show if authenticated and traveler exists */}
              {isAuthenticated && travelerExists && hasCart && hasPaymentData && (
                <div className="pt-6 md:hidden">
                  <div className="space-y-2 text-xs">
                    <button
                      type={'button'}
                      onClick={handleCreateOrder}
                      disabled={!canBookOrder || creatingOrder || orderCreated}
                      className={clsx(
                        'w-full my-3 py-3 px-4 rounded-lg text-lg  transition-all duration-200',
                        'focus:outline-none focus:ring-2 focus:ring-offset-2',
                        {
                          'bg-gray-800 hover:bg-gray-700 text-white focus:ring-gray-500': canBookOrder && !isDark,
                          'bg-gray-800 hover:bg-gray-600 text-white focus:ring-gray-500': canBookOrder && isDark,
                          'bg-gray-300 text-gray-500 cursor-not-allowed': !canBookOrder
                        }
                      )}
                    >
                      {creatingOrder && 'Processing Order...'}
                      {orderCreated && 'Order Created Successfully!'}
                      {!creatingOrder && !orderCreated && 'Book now'}
                    </button>
                    
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className={rightColumnClassName}>
              {/* Cart updating indicator */}
              {isAuthenticated && (updatingCart || updatingSidebarCart) && (
                <LoadingState
                  className="border-none" message="Updating your cart..."
                />
              )}
              
              {/* Order Summary - Show when traveler exists and not updating */}
              {isAuthenticated && hasCart && travelerExists && !updatingCart && !updatingSidebarCart && (
                <OrderSummary 
                  formData={formData}
                  creatingOrder={creatingOrder}
                  orderCreated={orderCreated}
                  onBookNow={handleCreateOrder}
                />
              )}
            </div>
          </div>
        </div>

        {/* Footer with navigation buttons */}
        <div className="md:hidden mt-8">
          <ShippingFlowFooter
            onBack={() => onClickBack?.()}
            showBack={true}
            showPrimaryButton={false}
            showSecondaryButton={false}
            onPrimaryAction={() => onClickContinue?.()}
            primaryButtonText="Book Now"
            primaryButtonContainerClass="hidden md:block"
          />
        </div>
      </div>
      {/* On Behalf Of Modal */}
    </div>
  );
};

ShippingFlowPayment.propTypes = {
  /** Function called when form is submitted */
  onSubmit: PropTypes.func,
  
  /** Whether the booking can be completed (all required fields filled) */
  canCompleteBooking: PropTypes.bool.isRequired,

  onClickContinue: PropTypes.func,
  onClickBack: PropTypes.func,
  canShowBack: PropTypes.func,
  onEditLeg: PropTypes.func,
};

ShippingFlowPayment.defaultProps = {
  canProceedFromProducts: false,
  canCompleteBooking: false 
};

export default ShippingFlowPayment;